import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Steps,
  Progress,
  Switch,
  Button,
  Typography,
  Spin,
  Space,
  Popconfirm,
  message,
  Card,
  Alert,
} from 'antd';
import {
  getOnboardingChecklist,
  overrideChecklistField,
  initiateBackgroundCheck,
  type OnboardingChecklistDto,
} from '../../services/adminKycApiService';

const { Title, Text } = Typography;

interface StepDef {
  label: string;
  field: keyof OnboardingChecklistDto;
}

const STEPS: StepDef[] = [
  { label: 'KYC Approved', field: 'kycApproved' },
  { label: 'Profile Complete', field: 'profileComplete' },
  { label: 'Bank Account Verified', field: 'bankVerified' },
  { label: 'Trade Certificate Uploaded', field: 'tradeCertUploaded' },
  { label: 'Background Check Passed', field: 'backgroundCheckPassed' },
  { label: 'Terms Accepted', field: 'termsAccepted' },
  { label: 'Onboarded', field: 'onboardingComplete' },
];

/** Provider onboarding progress page — shows checklist steps with admin override controls. */
export function ProviderOnboardingDetailPage() {
  const { providerId } = useParams<{ providerId: string }>();
  const [checklist, setChecklist] = useState<OnboardingChecklistDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const [bgCheckLoading, setBgCheckLoading] = useState(false);

  const adminRole = localStorage.getItem('adminRole');
  const isSuperAdmin = adminRole === 'SUPER_ADMIN';

  useEffect(() => {
    if (!providerId) return;
    setLoading(true);
    getOnboardingChecklist(providerId)
      .then(setChecklist)
      .catch(() => { void message.error('Failed to load onboarding checklist'); })
      .finally(() => setLoading(false));
  }, [providerId]);

  const handleToggle = async (field: keyof OnboardingChecklistDto, value: boolean) => {
    if (!providerId) return;
    setToggleLoading(field);
    try {
      const updated = await overrideChecklistField(providerId, field, value);
      setChecklist(updated);
      void message.success('Field updated successfully');
    } catch {
      void message.error('Failed to update field');
    } finally {
      setToggleLoading(null);
    }
  };

  const handleBackgroundCheck = async () => {
    if (!providerId) return;
    setBgCheckLoading(true);
    try {
      const result = await initiateBackgroundCheck(providerId);
      if (result.passed) {
        void message.success('Background check passed');
      } else {
        void message.warning('Background check did not pass');
      }
      // Refetch checklist
      const updated = await getOnboardingChecklist(providerId);
      setChecklist(updated);
    } catch {
      void message.error('Failed to initiate background check');
    } finally {
      setBgCheckLoading(false);
    }
  };

  const completedCount = checklist
    ? STEPS.filter((s) => checklist[s.field]).length
    : 0;

  const completionPercent = Math.round((completedCount / STEPS.length) * 100);

  if (!providerId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="No provider ID specified" />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!checklist) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Failed to load checklist" />
      </div>
    );
  }

  const stepsItems = STEPS.map((step) => {
    const complete = checklist[step.field] as boolean;
    const description = isSuperAdmin ? (
      <Space>
        <Popconfirm
          title={`Set "${step.label}" to ${!complete ? 'complete' : 'incomplete'}?`}
          onConfirm={() => handleToggle(step.field, !complete)}
          okText="Yes"
          cancelText="No"
        >
          <Switch
            size="small"
            checked={complete}
            onChange={() => { /* controlled via Popconfirm */ }}
            loading={toggleLoading === step.field}
          />
        </Popconfirm>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {complete ? 'Complete' : 'Pending'}
        </Text>
      </Space>
    ) : (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {complete ? 'Complete' : 'Pending'}
      </Text>
    );

    return {
      title: step.label,
      status: (complete ? 'finish' : 'wait') as 'finish' | 'wait',
      description,
    };
  });

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Provider Onboarding
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Provider ID: <Text code style={{ fontSize: 12 }}>{providerId}</Text>
      </Text>

      {/* Overall progress */}
      <Card bordered={false} style={{ borderRadius: 10, marginBottom: 24 }}>
        <Text strong>Overall completion</Text>
        <Progress
          percent={completionPercent}
          strokeColor={completionPercent === 100 ? '#52c41a' : '#1677ff'}
          style={{ marginTop: 8 }}
        />
        <Text type="secondary">
          {completedCount} of {STEPS.length} steps complete
        </Text>
      </Card>

      {/* Steps */}
      <Card bordered={false} style={{ borderRadius: 10, marginBottom: 24 }}>
        <Steps direction="vertical" items={stepsItems} />
      </Card>

      {/* Background check button */}
      {!checklist.backgroundCheckPassed && (
        <Button
          type="default"
          loading={bgCheckLoading}
          onClick={handleBackgroundCheck}
          style={{ marginBottom: 8 }}
        >
          Initiate Background Check
        </Button>
      )}
    </div>
  );
}

export default ProviderOnboardingDetailPage;
